import { hashKey, keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFireContext } from "../context";
import type { ApiClient } from "./client";
import { endpoints } from "./endpoints";
import { lockStore, type Lock } from "./locks";
import { ApiProblem } from "./problem";
import { isDefiniteRefusal, useIdempotencyKey } from "./useIdempotencyKey";

/** Every FER query key is ["fer", partyId, auth, ...]; configuration is kept for the session. */
const ROOT = "fer";

export interface QueryOptions {
  enabled?: boolean;
  staleTime?: number;
  keepPrevious?: boolean;
  /** Poll every this many ms while the screen is shown (queue, board, live map); 0 or undefined = no polling. */
  refetchInterval?: number;
}

/**
 * When each read's data was received, on the module's clock (review R2-2): recorded in the query function, so "Not
 * refreshed since" and stopped clocks show the time of the data, never the time a view was drawn.
 */
const RECEIVED = new WeakMap<QueryClient, Map<string, Date>>();
function receivedStore(qc: QueryClient): Map<string, Date> {
  let m = RECEIVED.get(qc);
  if (!m) {
    m = new Map();
    RECEIVED.set(qc, m);
  }
  return m;
}

/** A read of the FER API, cached under ["fer", partyId, auth, ...key], with `receivedAt` (module time of its data). */
export function useApi<R>(key: readonly unknown[], fn: (api: ApiClient) => Promise<R>, opts: QueryOptions = {}) {
  const { api, principal, now } = useFireContext();
  const qc = useQueryClient();
  // The signed-in person (and how they are signed in) is part of every key: a shared QueryClient never shows one
  // person's scoped data to the next person signed in on the same console, nor a realm session's data to a local
  // session that may not read it.
  const queryKey = [ROOT, principal.partyId, principal.auth ?? "realm", ...key];
  const hash = hashKey(queryKey);
  const result = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await fn(api);
      receivedStore(qc).set(hash, now());
      return data;
    },
    enabled: opts.enabled ?? true,
    staleTime: opts.staleTime,
    placeholderData: opts.keepPrevious ? keepPreviousData : undefined,
    refetchInterval: opts.refetchInterval ? opts.refetchInterval : false,
  });
  // Placeholder data (keepPrevious) belongs to another key: it has no receipt time of its own.
  const receivedAt = result.isPlaceholderData ? null : (receivedStore(qc).get(hash) ?? null);
  return Object.assign(result, { receivedAt });
}

function useInvalidateAll() {
  const qc = useQueryClient();
  // After a write, refresh everything this module shows except the configuration (writes never change it).
  return () => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === ROOT && q.queryKey[3] !== "config" });
}

/** Whether a write's outcome is unknown (network error, lost reply, 5xx, 408, 429, still in flight). */
export function isUnknownOutcome(error: unknown): boolean {
  return error instanceof ApiProblem && !isDefiniteRefusal(error);
}

/**
 * A write without an Idempotency-Key in the contract. Such writes are guarded by the record's state on the server
 * (an alert already handled, a dispatch already final), so resending is safe; after an unknown outcome the screen's
 * data is refreshed at once so the person sees whether it went through before trying again (`unknown`).
 */
export function useWrite<V, R>(fn: (api: ApiClient, vars: V) => Promise<R>) {
  const { api } = useFireContext();
  const invalidate = useInvalidateAll();
  const m = useMutation({
    mutationFn: (vars: V) => fn(api, vars),
    onSuccess: () => void invalidate(),
    onError: (e) => {
      if (isUnknownOutcome(e)) void invalidate();
    },
  });
  return { ...m, unknown: m.isError && isUnknownOutcome(m.error) };
}

/**
 * A write the contract requires an Idempotency-Key for. The key belongs to the component (form or dialog) that
 * holds this hook: the same key while the variables are unchanged, so resubmitting after a network error or a lost
 * reply never applies the write twice (the server replays the first answer); a new key after success, or after a
 * definite 4xx refusal.
 *
 * After an unknown outcome (network error, lost reply, 5xx, 408, 429, still in flight) the request is **locked**:
 * every later submit sends exactly the same variables with the same key, whatever the form now holds, until the
 * person explicitly discards it (`discard()`), because the first request may already have been applied. The lock is
 * kept per person and `intent` (operation + subject) with the QueryClient, so it survives closing and reopening the
 * dialog. It also keeps the whole form that produced the request (pass it with `remember(form)` before `mutate`);
 * `lockedForm` gives it back so a reopened dialog shows exactly the locked request, read-only (TRN RW1, RW2, RW9).
 */
export function useKeyedWrite<V, R>(fn: (api: ApiClient, vars: V, idempotencyKey: string) => Promise<R>, intent: string) {
  const { api, principal } = useFireContext();
  const qc = useQueryClient();
  const invalidate = useInvalidateAll();
  const keys = useIdempotencyKey();
  const store = lockStore(qc);
  const id = `${principal.partyId}|${principal.auth ?? "realm"}|${intent}`;
  const [locked, setLocked] = useState(() => store.has(id));
  const used = useRef<Lock | null>(null);
  const form = useRef<unknown>(undefined);
  const m = useMutation({
    mutationFn: (vars: V) => {
      const lock = store.get(id);
      const sent: Lock = lock ?? { vars, key: keys.keyFor(vars), form: form.current };
      used.current = sent;
      return fn(api, sent.vars as V, sent.key);
    },
    onSuccess: () => {
      keys.reset();
      store.delete(id);
      setLocked(false);
      void invalidate();
    },
    onError: (e) => {
      keys.onError(e);
      if (isDefiniteRefusal(e)) {
        store.delete(id);
        setLocked(false);
      } else {
        if (!store.has(id) && used.current) store.set(id, used.current);
        setLocked(true);
      }
    },
  });
  const reset = m.reset;
  const discard = useCallback(() => {
    store.delete(id);
    keys.reset();
    setLocked(false);
    reset();
  }, [store, id, keys, reset]);
  /** The form values to keep with the lock if this submission's outcome is unknown (call before `mutate`). */
  const remember = useCallback((values: unknown) => {
    form.current = values;
  }, []);
  const lockedForm: unknown = locked ? store.get(id)?.form : undefined;
  return { ...m, locked, discard, remember, lockedForm };
}

/** The current time, re-read every `tickMs` (0: read once; tests pin the clock). Drives the live clocks. */
export function useNow(): Date {
  const { now, clockTickMs } = useFireContext();
  const [at, setAt] = useState(() => now());
  useEffect(() => {
    if (!clockTickMs) return;
    const t = setInterval(() => setAt(now()), clockTickMs);
    return () => clearInterval(t);
  }, [now, clockTickMs]);
  return clockTickMs ? at : now();
}

/**
 * Whether a read shows old data because its latest refresh failed (review FW1), and when it was last refreshed (on the
 * module's clock). Live clocks stop at that moment while stale: they never claim to be live on data that is not.
 */
export function useFreshness(q: { data: unknown; error: unknown; receivedAt: Date | null }): { stale: boolean; refreshedAt: Date | null } {
  return { stale: !!q.error && q.data !== undefined, refreshedAt: q.receivedAt };
}

// ------------------------------------------------------------------ shared reads

export function useConfig() {
  return useApi(["config"], endpoints.getConfig, { staleTime: 10 * 60_000 });
}

export function useStations(enabled = true) {
  return useApi(["stations", {}], (api) => endpoints.listStations(api), { enabled, staleTime: 60_000 });
}
